import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Copy, Trash2, Plus, ExternalLink, Loader2 } from "lucide-react";
import { DISCIPLINE_CODES } from "../data/disciplines";

export default function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [org, setOrg] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteDiscipline, setInviteDiscipline] = useState("");
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.org_role === "admin";

  const load = async () => {
    try {
      const [m, i, o] = await Promise.all([
        api.get("/org/members"),
        api.get("/org/invites"),
        api.get("/org/me"),
      ]);
      setMembers(m.data);
      setInvites(i.data);
      setOrg(o.data?.org || null);
    } catch {
      toast.error("Could not load team");
    }
  };

  useEffect(() => {
    if (isAdmin) load();
     
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="border border-zinc-200 rounded-xl p-10 text-center" data-testid="team-not-admin">
        <p className="text-zinc-500">Only your organization admin can manage the team.</p>
      </div>
    );
  }

  const createInvite = async () => {
    setBusy(true);
    try {
      await api.post("/org/invites", {
        email: inviteEmail || null,
        discipline: inviteDiscipline || null,
        origin_url: window.location.origin,
      });
      setInviteEmail("");
      setInviteDiscipline("");
      load();
      toast.success("Invite created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create invite");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/org/invites/${id}`);
      setInvites((p) => p.filter((x) => x.id !== id));
    } catch {
      toast.error("Could not revoke");
    }
  };

  const removeMember = async (uid) => {
    if (!confirm("Remove this team member? They will lose access immediately.")) return;
    try {
      await api.delete(`/org/members/${uid}`);
      setMembers((m) => m.filter((x) => x.id !== uid));
      load();
      toast.success("Removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove");
    }
  };

  const inviteUrl = (token) => `${window.location.origin}/register?invite=${token}`;

  const copyInviteLink = (token) => {
    navigator.clipboard.writeText(inviteUrl(token));
    toast.success("Invite link copied");
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("origin_url", window.location.origin);
      const { data } = await api.post("/billing/portal", fd);
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open portal");
      setBusy(false);
    }
  };

  const seatsUsed = (org?.member_count || 0) + invites.filter((i) => !i.used).length;
  const seatsTotal = org?.seats || 0;
  const seatsAvailable = Math.max(0, seatsTotal - seatsUsed);

  return (
    <div className="space-y-8" data-testid="team-pane">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading text-2xl tracking-tight text-zinc-900">Team</h3>
          <p className="text-sm text-zinc-600 mt-1">
            {org?.name} · {seatsUsed}/{seatsTotal} seats used · {seatsAvailable} available
          </p>
        </div>
        <Button variant="outline" onClick={openPortal} disabled={busy} data-testid="team-portal-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ExternalLink className="w-4 h-4 mr-2" /> Manage billing</>}
        </Button>
      </div>

      {/* Invite form */}
      <div className="border border-zinc-200 rounded-xl bg-white p-5">
        <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-3">Invite a team member</p>
        <div className="flex gap-3 items-end flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <Label className="text-xs text-zinc-500">Email (optional — leave blank for an open invite link)</Label>
            <Input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="colleague@clinic.com"
              className="mt-1 h-11"
              data-testid="invite-email-input"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Role (optional — pre-configures their account)</Label>
            <div className="mt-1 inline-flex gap-1 p-1 border border-zinc-200 rounded-lg">
              <button
                type="button"
                onClick={() => setInviteDiscipline("")}
                className={`h-9 px-3 text-xs rounded-md font-medium ${inviteDiscipline === "" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
                data-testid="invite-discipline-any"
              >Any</button>
              {DISCIPLINE_CODES.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setInviteDiscipline(d)}
                  className={`h-9 px-3 text-xs rounded-md font-medium ${inviteDiscipline === d ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
                  data-testid={`invite-discipline-${d}`}
                >{d}</button>
              ))}
            </div>
          </div>
          <Button onClick={createInvite} disabled={busy || seatsAvailable <= 0} className="h-11" data-testid="create-invite-button">
            <Plus className="w-4 h-4 mr-2" /> Create invite
          </Button>
        </div>
        {seatsAvailable <= 0 && (
          <p className="text-xs text-amber-700 mt-3">All seats taken. Add more from the billing portal to invite more people.</p>
        )}
      </div>

      {/* Pending invites */}
      <div>
        <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-3">Pending invites</p>
        {invites.filter((i) => !i.used).length === 0 ? (
          <p className="text-sm text-zinc-500" data-testid="no-pending-invites">No pending invites.</p>
        ) : (
          <div className="space-y-2">
            {invites.filter((i) => !i.used).map((inv) => (
              <div key={inv.id} className="flex items-center justify-between gap-3 border border-zinc-200 rounded-lg p-3 bg-white" data-testid={`invite-row-${inv.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-zinc-900">
                    {inv.email || "Open invite (any email)"}
                    {inv.discipline && <span className="ml-2 inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700">{inv.discipline}</span>}
                  </p>
                  <p className="text-xs text-zinc-400 truncate">{inviteUrl(inv.token)}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => copyInviteLink(inv.token)} data-testid={`copy-invite-${inv.id}`}>
                  <Copy className="w-4 h-4" />
                </Button>
                <button
                  onClick={() => revoke(inv.id)}
                  className="p-2 text-zinc-400 hover:text-red-600 transition-colors"
                  data-testid={`revoke-invite-${inv.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Members */}
      <div>
        <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-3">Members</p>
        <div className="space-y-2">
          {members.map((m) => (
            <div key={m.id} className="flex items-center justify-between gap-3 border border-zinc-200 rounded-lg p-3 bg-white" data-testid={`member-row-${m.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-zinc-900 font-medium">{m.full_name}</p>
                <p className="text-xs text-zinc-500">{m.email} · {m.discipline} · {m.org_role}</p>
              </div>
              {m.org_role !== "admin" && (
                <button
                  onClick={() => removeMember(m.id)}
                  className="p-2 text-zinc-400 hover:text-red-600 transition-colors"
                  data-testid={`remove-member-${m.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
